import type { components } from "@cntrl/api-client";
import type { TimelineItem, TourItem, YearGroup } from "./bookingRows.js";

type ShowMoneyRow = components["schemas"]["ShowMoneyRow"];

/**
 * The money half of the Booking rows — deliberately a SEPARATE module from the money-free
 * `bookingRows.ts`, mirroring the backend's separate `Cntrl.Core.Modules.Artist.Money` namespace. Nothing
 * here is imported by the timeline builder: the rows are assembled money-free and the figures are looked up
 * afterwards, so a money-free seat renders the identical list with these functions never called.
 *
 * Every figure arrives PRE-COMPUTED from the server's one `ShowPnl` authority. The only arithmetic here is
 * SUMMING rows that are already correct — never re-deriving a P&L, which is how the MVP shipped three
 * different profit numbers on three different surfaces.
 */
export type MoneyByShow = ReadonlyMap<string, ShowMoneyRow>;

/** `$1,234` — whole dollars, the reference's row/tile format. */
export function usdCents(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** `+$1,234` / `−$1,234` — a P&L always carries its sign, and the minus is U+2212, not a hyphen. */
export function signedUsdCents(cents: number | null | undefined): string {
  const value = cents ?? 0;
  return (value < 0 ? "−" : "+") + usdCents(Math.abs(value));
}

/** Which way a P&L figure leans, for the up/down tint. Exactly 0 is neither. */
export function pnlTone(cents: number | null | undefined): "up" | "down" | undefined {
  const value = cents ?? 0;
  return value > 0 ? "up" : value < 0 ? "down" : undefined;
}

/** Every show id under a timeline entry — a tour contributes all of its dates, an off day none. */
function showIdsOf(item: TimelineItem): string[] {
  if (item.kind === "show") return item.show.showId ? [item.show.showId] : [];
  if (item.kind === "tour") {
    return item.children.flatMap((child) =>
      child.kind === "show" && child.show.showId ? [child.show.showId] : [],
    );
  }
  return [];
}

function sum(items: readonly TimelineItem[], money: MoneyByShow, pick: (row: ShowMoneyRow) => number | null | undefined) {
  let total = 0;
  for (const item of items) {
    for (const id of showIdsOf(item)) {
      const row = money.get(id);
      if (row) total += pick(row) ?? 0;
    }
  }
  return total;
}

/**
 * `Tour total $X` — the summed GUARANTEE across a tour's dates.
 *
 * Returns null when nothing is booked, because the reference renders this block only under `total > 0`: a
 * tour whose dates have no guarantee yet shows no total rather than a `$0` that reads like a free tour. That
 * conditional is the spec's actual behaviour and appears ONLY in its row-anatomy specimen — the prose never
 * mentions the block at all, so it is taken from the legacy source.
 */
export function tourTotalCents(group: TourItem, money: MoneyByShow): number | null {
  const total = sum([group], money, (row) => row.guaranteeCents);
  return total > 0 ? total : null;
}

/**
 * The `· P&L +$X` half of a year header — the sum of each show's YEAR figure, which INCLUDES flights and
 * per diem while the rows beneath it exclude them. That divergence is the documented legacy quirk; see
 * `ShowPnl` on the server for why it can only ever appear on an unsettled show.
 */
export function yearPnlCents(group: YearGroup, money: MoneyByShow): number {
  return sum(group.items, money, (row) => row.yearPnlCents);
}

/**
 * The `+$X` pill on a PAST tour header — the sum of its dates' ROW figures, not their year figures.
 *
 * This is a third P&L rule in one accordion and it is genuinely what the legacy page does (`PastTourRow`
 * reduces over `rowPnl`), so a tour's pill and its parent year header can disagree by the tour's travel
 * costs. Specimen-only in the spec; recorded here because a reader will otherwise "fix" it.
 */
export function tourPnlCents(group: TourItem, money: MoneyByShow): number {
  return sum([group], money, (row) => row.rowPnlCents);
}

/** The Past row's four stats. `Total profit` IS the row P&L and `Profit` is that minus merch — derived one
 * from the other so the rows keep summing to the year header exactly. A merch or ticket figure that was
 * never recorded renders as `-`, never as `$0`/`0`. */
export interface PastRowStats {
  profitCents: number;
  merchCents: number | null;
  totalProfitCents: number;
  ticketsSold: number | null;
}

export function pastRowStats(row: ShowMoneyRow | undefined): PastRowStats {
  const total = row?.rowPnlCents ?? 0;
  const merch = row?.merchCents ?? null;
  return {
    profitCents: total - (merch ?? 0),
    merchCents: merch,
    totalProfitCents: total,
    ticketsSold: row?.ticketsSold ?? null,
  };
}
